import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCode,
  FaDownload,
  FaEdit,
  FaKey,
  FaLink,
  FaMobileAlt,
  FaPlus,
  FaPowerOff,
  FaQrcode,
  FaSyncAlt,
  FaTrashAlt,
  FaTv,
  FaUpload,
} from "react-icons/fa";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import AddKioskModal from "../../components/Kiosk/AddKioskModal";
import SecretModal from "../../components/Kiosk/SecretModal";
import WebQrModal from "../../components/Kiosk/WebQrModal";
import WebLinkModal from "../../components/Kiosk/WebLinkModal";
import S710DevicesModal from "../../components/Kiosk/S710DevicesModal";
import { ImpactEmbedModal } from "../../components/Kiosk/ImpactEmbedModal";
import { PageLayout } from "../../components/layout";
import Pagination from "../../components/Pagination";
import { Table, TableColumn } from "../../components/Table";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import {
  getKioskApkInfo,
  KioskApkInfo,
  uploadKioskApk,
} from "../../lib/firebase/firebaseStore";
import {
  createKiosk,
  deleteKiosk,
  generateKioskWebLink,
  getKiosks,
  rotateKioskSecret,
  updateKiosk,
  updateKioskStatus,
} from "../../services/KioskService";
import { CreateKioskRequest, Kiosk } from "../../types/kiosk.type";
import { useCurrentEmail, useIsKioskOwner } from "../../hooks/useCurrentRole";
import config from "../../config";

type SecretModalState = {
  open: boolean;
  title: string;
  secret: string;
};

type IconTone = "default" | "danger" | "success" | "warning";

const PAGE_SIZE = 10;
const APK_UPLOAD_ALLOWED_EMAIL = "qr0003@mailinator.com";

/** Short signage URL — the TV resolves its own credentials from the kiosk id. */
function buildTvLink(webLink: string, kioskId: string): string {
  const trimmed = webLink.trim();

  try {
    const url = new URL(trimmed);
    url.pathname = `/tv/${encodeURIComponent(kioskId)}`;
    url.search = "";
    return url.toString();
  } catch {
    return trimmed;
  }
}

const ActionIconButton: React.FC<{
  label: string;
  icon: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  tone?: IconTone;
  onClick: () => void;
}> = ({ label, icon, loading = false, disabled = false, tone = "default", onClick }) => {
  const toneClassMap: Record<IconTone, string> = {
    default:
      "border-gray-200 text-gray-600 hover:border-primary-100/40 hover:bg-primary-10 hover:text-primary-100 dark:border-white/10 dark:text-gray-300 dark:hover:border-primary-100/40 dark:hover:bg-slate-800",
    success:
      "border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-600/30 dark:text-emerald-300 dark:hover:bg-emerald-800/20",
    warning:
      "border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-600/30 dark:text-amber-300 dark:hover:bg-amber-800/20",
    danger:
      "border-red-200 text-red-700 hover:bg-red-50 dark:border-red-600/30 dark:text-red-300 dark:hover:bg-red-900/20",
  };

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border bg-white transition-all dark:bg-slate-900/30 ${toneClassMap[tone]} ${
        disabled || loading ? "cursor-not-allowed opacity-60" : ""
      }`}
    >
      {loading ? (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
          <path
            d="M22 12a10 10 0 0 1-10 10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            className="opacity-90"
          />
        </svg>
      ) : (
        icon
      )}
    </button>
  );
};

const KioskManagementPage: React.FC = () => {
  const isKioskOwner = useIsKioskOwner();
  const currentEmail = useCurrentEmail();
  const canUploadApk = currentEmail?.trim().toLowerCase() === APK_UPLOAD_ALLOWED_EMAIL;
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [kioskTypeFilter, setKioskTypeFilter] = useState<"" | "PHYSICAL" | "DIGITAL" | "BOTH">("");
  const [activeFilter, setActiveFilter] = useState<"" | "active" | "inactive">("");
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPagination, setResetPagination] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingKiosk, setEditingKiosk] = useState<Kiosk | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);
  const [rotateTarget, setRotateTarget] = useState<Kiosk | null>(null);
  const [s710DevicesTarget, setS710DevicesTarget] = useState<Kiosk | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Kiosk | null>(null);
  const [rotateTokenExpiryHours, setRotateTokenExpiryHours] = useState<number>(1);
  const [rotateValidationError, setRotateValidationError] = useState<string>("");

  const [secretModal, setSecretModal] = useState<SecretModalState>({
    open: false,
    title: "",
    secret: "",
  });
  const [webLinkModal, setWebLinkModal] = useState<{
    open: boolean;
    title: string;
    webLink: string;
    kioskSecret: string;
    impactShareUrl?: string;
  }>({
    open: false,
    title: "",
    webLink: "",
    kioskSecret: "",
    impactShareUrl: "",
  });
  const [webQrModal, setWebQrModal] = useState<{
    open: boolean;
    title: string;
    webLink: string;
    kioskSecret: string;
  }>({
    open: false,
    title: "",
    webLink: "",
    kioskSecret: "",
  });
  const [impactEmbedModal, setImpactEmbedModal] = useState<{
    open: boolean;
    kioskId: string;
    kioskName: string;
    widgetUrl: string;
  }>({
    open: false,
    kioskId: "",
    kioskName: "",
    widgetUrl: "",
  });
  const [apkDownloadUrl, setApkDownloadUrl] = useState("");
  const [apkLoading, setApkLoading] = useState(false);
  const [apkUploading, setApkUploading] = useState(false);
  const [apkVersionDraft, setApkVersionDraft] = useState("");
  const [apkInfo, setApkInfo] = useState<KioskApkInfo | null>(null);
  const apkFileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchKiosks = async () => {
    setLoadingList(true);
    setListError("");

    try {
      const response = await getKiosks();
      setKiosks(response);
    } catch (error) {
      const message = (error as Error).message || "Failed to load kiosks";
      setListError(message);
      showErrorToast(message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchKiosks();
  }, []);

  const loadKioskApk = async () => {
    setApkLoading(true);
    try {
      const info = await getKioskApkInfo();
      setApkDownloadUrl(info.downloadUrl);
      setApkInfo(info);
    } catch {
      setApkDownloadUrl("");
      setApkInfo(null);
    } finally {
      setApkLoading(false);
    }
  };

  useEffect(() => {
    loadKioskApk();
  }, []);

  const filteredKiosks = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return kiosks.filter((kiosk) => {
      const source = [
        kiosk.kioskId,
        kiosk.name,
        kiosk.location,
        kiosk.region,
        kiosk.ownerEmail,
        kiosk.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (normalizedSearch && !source.includes(normalizedSearch)) {
        return false;
      }

      if (regionFilter && (kiosk.region || "") !== regionFilter) {
        return false;
      }

      if (kioskTypeFilter && (kiosk.kioskType || "PHYSICAL") !== kioskTypeFilter) {
        return false;
      }

      if (activeFilter === "active" && !kiosk.active) {
        return false;
      }

      if (activeFilter === "inactive" && kiosk.active) {
        return false;
      }

      return true;
    });
  }, [kiosks, searchTerm, regionFilter, kioskTypeFilter, activeFilter]);

  const regionOptions = useMemo(
    () => Array.from(new Set(kiosks.map((item) => item.region).filter(Boolean) as string[])).sort(),
    [kiosks],
  );

  const paginatedKiosks = useMemo(
    () => filteredKiosks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredKiosks, currentPage],
  );

  useEffect(() => {
    setCurrentPage(1);
    setResetPagination((value) => !value);
  }, [filteredKiosks]);

  const showSecret = (title: string, plainSecret: string) => {
    setSecretModal({
      open: true,
      title,
      secret: plainSecret,
    });
  };

  const handleCreateKiosk = async (payload: CreateKioskRequest) => {
    setCreating(true);
    setCreateError("");

    try {
      const result = await createKiosk(payload);
      await fetchKiosks();
      setShowAddModal(false);
      showSuccessToast("Kiosk created successfully");

      if (result.plainSecret) {
        const secretAndPairingCode = result.pairingCode
          ? `Kiosk Secret:\n${result.plainSecret}\n\nS710 Printed Activation Code (valid until device is unpaired):\n${result.pairingCode}`
          : result.plainSecret;
        showSecret("Save your secret & S710 pairing code", secretAndPairingCode);
      } else {
        showErrorToast("Kiosk created, but no secret was returned by API");
      }
    } catch (error) {
      const message = (error as Error).message || "Failed to create kiosk";
      setCreateError(message);
      showErrorToast(message);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateKiosk = async (payload: CreateKioskRequest) => {
    if (!editingKiosk) {
      return;
    }

    setCreating(true);
    setCreateError("");

    try {
      await updateKiosk(editingKiosk.kioskId, {
        name: payload.name,
        location: payload.location,
        region: payload.region,
        description: payload.description,
        tokenExpiryHours: payload.tokenExpiryHours,
        ownerEmail: payload.ownerEmail,
        kioskType: payload.kioskType,
        kioskCostCents: payload.kioskCostCents,
        monthlyLicenseFeeCents: payload.monthlyLicenseFeeCents,
        commissionPercent: payload.commissionPercent,
      });

      await fetchKiosks();
      setEditingKiosk(null);
      showSuccessToast("Kiosk updated successfully");
    } catch (error) {
      const message = (error as Error).message || "Failed to update kiosk";
      setCreateError(message);
      showErrorToast(message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (kiosk: Kiosk) => {
    const actionKey = `status-${kiosk.kioskId}`;
    setActionLoadingKey(actionKey);

    try {
      await updateKioskStatus(kiosk.kioskId, { active: !kiosk.active });
      await fetchKiosks();
      showSuccessToast(
        `Kiosk ${!kiosk.active ? "enabled" : "disabled"} successfully`,
      );
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to update kiosk status");
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleRotateSecret = async () => {
    if (!rotateTarget) {
      return;
    }

    if (!Number.isInteger(rotateTokenExpiryHours) || rotateTokenExpiryHours < 1 || rotateTokenExpiryHours > 720) {
      setRotateValidationError("Token expiry hours must be between 1 and 720");
      return;
    }

    const actionKey = `rotate-${rotateTarget.kioskId}`;
    setActionLoadingKey(actionKey);

    try {
      const response = await rotateKioskSecret(rotateTarget.kioskId, {
        tokenExpiryHours: rotateTokenExpiryHours,
      });
      setRotateTarget(null);
      setRotateValidationError("");
      showSuccessToast("Kiosk secret rotated successfully");

      if (response.plainSecret) {
        showSecret("Save your secret", response.plainSecret);
      } else {
        showErrorToast("Secret rotated, but no secret was returned by API");
      }
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to rotate kiosk secret");
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDeleteKiosk = async () => {
    if (!deleteTarget) {
      return;
    }

    const actionKey = `delete-${deleteTarget.kioskId}`;
    setActionLoadingKey(actionKey);

    try {
      await deleteKiosk(deleteTarget.kioskId);
      setDeleteTarget(null);
      await fetchKiosks();
      showSuccessToast("Kiosk deleted successfully");
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to delete kiosk");
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleGenerateWebLink = async (kiosk: Kiosk) => {
    const actionKey = `weblink-${kiosk.kioskId}`;
    setActionLoadingKey(actionKey);

    try {
      const response = await generateKioskWebLink(kiosk.kioskId);
      const impactShareUrl = `https://kiosk.trackyourimpact.io/impact/${encodeURIComponent(kiosk.kioskId)}`;
      setWebLinkModal({
        open: true,
        title: `Links for ${kiosk.name || kiosk.kioskId}`,
        webLink: response.webLink,
        kioskSecret: response.kioskSecret,
        impactShareUrl,
      });
      showSuccessToast("Kiosk links generated");
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to generate kiosk web link");
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleGenerateQr = async (kiosk: Kiosk) => {
    const actionKey = `qr-${kiosk.kioskId}`;
    setActionLoadingKey(actionKey);

    try {
      const response = await generateKioskWebLink(kiosk.kioskId);
      setWebQrModal({
        open: true,
        title: `QR for ${kiosk.name || kiosk.kioskId}`,
        webLink: response.webLink,
        kioskSecret: response.kioskSecret,
      });
      showSuccessToast("Kiosk QR generated");
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to generate kiosk QR");
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleCopyTvLink = async (kiosk: Kiosk) => {
    const actionKey = `tvlink-${kiosk.kioskId}`;
    setActionLoadingKey(actionKey);

    try {
      const response = await generateKioskWebLink(kiosk.kioskId);
      const tvLink = buildTvLink(response.webLink, kiosk.kioskId);
      await navigator.clipboard.writeText(tvLink);
      showSuccessToast(`TV link copied for ${kiosk.name || kiosk.kioskId}.`);
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to copy TV link");
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleOpenImpactEmbed = (kiosk: Kiosk) => {
    const widgetUrl = `${config.apiBaseUrl}/kiosks/${encodeURIComponent(kiosk.kioskId)}/impact-widget`;
    setImpactEmbedModal({
      open: true,
      kioskId: kiosk.kioskId,
      kioskName: kiosk.name || kiosk.kioskId,
      widgetUrl,
    });
  };

  const columns: TableColumn<Kiosk>[] = [
      {
        id: "kioskId",
        label: "Kiosk ID",
        width: "16%",
        priority: "high",
      },
      {
        id: "name",
        label: "Name",
        width: "20%",
        priority: "high",
      },
      {
        id: "ownerEmail",
        label: "Owner",
        width: "14%",
        priority: "medium",
        format: (value) => (value ? String(value) : "-"),
      },
      {
        id: "kioskType",
        label: "Type",
        width: "10%",
        priority: "medium",
        format: (value) => (value ? String(value) : "PHYSICAL"),
      },
      {
        id: "active",
        label: "Status",
        width: "10%",
        priority: "medium",
        format: (value) => (
          <Badge
            label={value ? "Active" : "Inactive"}
            variant={value ? "success" : "default"}
            size="md"
          />
        ),
      },
      {
        id: "tokenExpiryHours",
        label: "Token Expiry",
        width: "10%",
        priority: "medium",
        format: (value) => `${Number(value || 1)}h`,
      },
      {
        id: "actions",
        label: "Actions",
        width: "30%",
        priority: "high",
        type: "actions",
        format: (_, row) => (
          <div className="flex flex-wrap items-center gap-2" onClick={(event) => event.stopPropagation()}>
            {!isKioskOwner && (
              <ActionIconButton
                label={row.active ? "Disable kiosk" : "Enable kiosk"}
                icon={<FaPowerOff className="h-4 w-4" />}
                tone={row.active ? "warning" : "success"}
                loading={actionLoadingKey === `status-${row.kioskId}`}
                disabled={actionLoadingKey !== null && actionLoadingKey !== `status-${row.kioskId}`}
                onClick={() => handleToggleStatus(row)}
              />
            )}
            <ActionIconButton
              label="Edit kiosk"
              icon={<FaEdit className="h-4 w-4" />}
              loading={actionLoadingKey === `edit-${row.kioskId}`}
              disabled={actionLoadingKey !== null && actionLoadingKey !== `edit-${row.kioskId}`}
              onClick={() => {
                setCreateError("");
                setEditingKiosk(row);
              }}
            />
            <ActionIconButton
              label="Rotate secret"
              icon={<FaKey className="h-4 w-4" />}
              loading={actionLoadingKey === `rotate-${row.kioskId}`}
              disabled={actionLoadingKey !== null && actionLoadingKey !== `rotate-${row.kioskId}`}
              onClick={() => {
                setRotateTarget(row);
                setRotateTokenExpiryHours(Number(row.tokenExpiryHours || 1));
                setRotateValidationError("");
              }}
            />
            <ActionIconButton
              label="Copy link"
              icon={<FaLink className="h-4 w-4" />}
              loading={actionLoadingKey === `weblink-${row.kioskId}`}
              disabled={actionLoadingKey !== null && actionLoadingKey !== `weblink-${row.kioskId}`}
              onClick={() => handleGenerateWebLink(row)}
            />
            <ActionIconButton
              label={`Manage S710 devices for ${row.kioskId}`}
              icon={<FaMobileAlt className="h-4 w-4" />}
              disabled={actionLoadingKey !== null}
              onClick={() => setS710DevicesTarget(row)}
            />
            <ActionIconButton
              label="Copy TV link"
              icon={<FaTv className="h-4 w-4" />}
              loading={actionLoadingKey === `tvlink-${row.kioskId}`}
              disabled={actionLoadingKey !== null && actionLoadingKey !== `tvlink-${row.kioskId}`}
              onClick={() => {
                void handleCopyTvLink(row);
              }}
            />
            <ActionIconButton
              label="Download QR"
              icon={<FaQrcode className="h-4 w-4" />}
              loading={actionLoadingKey === `qr-${row.kioskId}`}
              disabled={actionLoadingKey !== null && actionLoadingKey !== `qr-${row.kioskId}`}
              onClick={() => handleGenerateQr(row)}
            />
            <ActionIconButton
              label="Live impact & embed"
              icon={<FaCode className="h-4 w-4" />}
              disabled={actionLoadingKey !== null}
              onClick={() => {
                handleOpenImpactEmbed(row);
              }}
            />
            {!isKioskOwner && (
              <>
                <ActionIconButton
                  label="Delete kiosk"
                  icon={<FaTrashAlt className="h-4 w-4" />}
                  tone="danger"
                  loading={actionLoadingKey === `delete-${row.kioskId}`}
                  disabled={actionLoadingKey !== null && actionLoadingKey !== `delete-${row.kioskId}`}
                  onClick={() => setDeleteTarget(row)}
                />
              </>
            )}
          </div>
        ),
      },
    ];

  const renderFilters = () => (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="grid w-full gap-2 md:grid-cols-2 lg:max-w-5xl lg:grid-cols-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search kiosk ID, name, region"
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-100/20 dark:border-white/15 dark:bg-slate-900/70 dark:text-gray-100"
        />

        <select
          value={regionFilter}
          onChange={(event) => setRegionFilter(event.target.value)}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 focus:border-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-100/20 dark:border-white/15 dark:bg-slate-900/70 dark:text-gray-100"
        >
          <option value="">All regions</option>
          {regionOptions.map((region) => (
            <option key={region} value={region}>
              {region}
            </option>
          ))}
        </select>

        <select
          value={kioskTypeFilter}
          onChange={(event) => setKioskTypeFilter(event.target.value as "" | "PHYSICAL" | "DIGITAL" | "BOTH")}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 focus:border-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-100/20 dark:border-white/15 dark:bg-slate-900/70 dark:text-gray-100"
        >
          <option value="">All types</option>
          <option value="PHYSICAL">Physical</option>
          <option value="DIGITAL">Digital</option>
          <option value="BOTH">Both</option>
        </select>

        <select
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value as "" | "active" | "inactive")}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 focus:border-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-100/20 dark:border-white/15 dark:bg-slate-900/70 dark:text-gray-100"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
        <span className="rounded-full bg-primary-10 px-3 py-1.5 font-semibold text-primary-100 dark:bg-slate-800 dark:text-primary-70">
          {filteredKiosks.length} kiosk{filteredKiosks.length === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );

  const renderPagination = () =>
    filteredKiosks.length > PAGE_SIZE ? (
      <Pagination
        totalRecords={filteredKiosks.length}
        perPageCount={PAGE_SIZE}
        reset={resetPagination}
        setPerPageDataSet={(start, _end) => {
          const nextPage = Math.max(1, Math.ceil(start / PAGE_SIZE));
          setCurrentPage(nextPage);
        }}
      />
    ) : null;

  const handleApkFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canUploadApk) {
      showErrorToast("Only qr0003@mailinator.com can upload the kiosk APK.");
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".apk")) {
      showErrorToast("Please select a valid .apk file.");
      return;
    }

    setApkUploading(true);
    try {
      const info = await uploadKioskApk(file, apkVersionDraft);
      setApkDownloadUrl(info.downloadUrl);
      setApkInfo(info);
      if (info.version) {
        setApkVersionDraft(info.version);
      }
      showSuccessToast("Kiosk APK uploaded successfully.");
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to upload APK");
    } finally {
      setApkUploading(false);
    }
  };

  return (
    <>
      <PageLayout
        title="Kiosk Management"
        subtitle="Create and manage kiosk credentials and status."
        filters={renderFilters()}
        footer={renderPagination()}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              startIcon={<FaSyncAlt />}
              onClick={fetchKiosks}
              loading={loadingList}
            >
              Refresh
            </Button>
            {!isKioskOwner && (
              <Button variant="primary" size="sm" startIcon={<FaPlus />} onClick={() => setShowAddModal(true)}>
                Add Kiosk
              </Button>
            )}
          </div>
        }
      >
        <div className="mx-4 mt-4 rounded-2xl border border-primary-100/20 bg-gradient-to-r from-primary-10 to-white p-4 dark:border-white/10 dark:from-slate-800 dark:to-slate-900">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Kiosk Android APK</p>
              {apkInfo ? (
                <div className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                  <p>File: {apkInfo.fileName || "latest.apk"}</p>
                  <p>Version: {apkInfo.version || "Not set"}</p>
                  <p>
                    Uploaded: {apkInfo.uploadedAt ? new Date(apkInfo.uploadedAt).toLocaleString() : "Unknown"}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {canUploadApk && (
                <>
                  <input
                    type="text"
                    value={apkVersionDraft}
                    onChange={(event) => setApkVersionDraft(event.target.value)}
                    placeholder="Version (e.g. 1.0.17)"
                    className="h-9 w-44 rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-800 focus:border-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-100/20 dark:border-white/15 dark:bg-slate-900/70 dark:text-gray-100"
                  />
                  <input
                    ref={apkFileInputRef}
                    type="file"
                    accept=".apk,application/vnd.android.package-archive"
                    className="hidden"
                    onChange={handleApkFileSelected}
                    disabled={apkUploading}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    startIcon={<FaUpload />}
                    loading={apkUploading}
                    disabled={apkUploading}
                    onClick={() => apkFileInputRef.current?.click()}
                  >
                    Upload APK
                  </Button>
                </>
              )}
              <Button
                variant="secondary"
                size="sm"
                startIcon={<FaDownload />}
                loading={apkLoading}
                disabled={!apkDownloadUrl || apkLoading}
                onClick={() => {
                  if (!apkDownloadUrl) {
                    showErrorToast("No APK uploaded yet.");
                    return;
                  }
                  window.open(apkDownloadUrl, "_blank", "noopener,noreferrer");
                }}
              >
                Download APK
              </Button>

              <Button
                variant="tertiary"
                size="sm"
                startIcon={<FaSyncAlt />}
                onClick={loadKioskApk}
                loading={apkLoading}
              >
                Refresh APK
              </Button>
            </div>
          </div>
        </div>

        {listError && (
          <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            {listError}
          </div>
        )}

        <div className="p-4">
          {isKioskOwner ? (
            <>
              <div className="grid gap-3 md:hidden">
                {loadingList ? (
                  <div className="rounded-2xl border border-gray-200 bg-white p-5 text-center text-sm text-gray-600 dark:border-white/10 dark:bg-slate-900/60 dark:text-gray-300">
                    Loading kiosks...
                  </div>
                ) : paginatedKiosks.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-5 text-center text-sm text-gray-600 dark:border-white/10 dark:bg-slate-900/60 dark:text-gray-300">
                    No kiosks found.
                  </div>
                ) : paginatedKiosks.map((kiosk) => (
                  <article key={kiosk.kioskId} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">{kiosk.name || kiosk.kioskId}</h2>
                        <p className="mt-1 break-all font-mono text-xs text-gray-500 dark:text-gray-400">{kiosk.kioskId}</p>
                      </div>
                      <Badge
                        label={kiosk.active ? "Active" : "Inactive"}
                        variant={kiosk.active ? "success" : "default"}
                        size="md"
                      />
                    </div>
                    <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-gray-100 pt-3 text-sm dark:border-white/10">
                      <div>
                        <dt className="text-xs text-gray-500 dark:text-gray-400">Location</dt>
                        <dd className="mt-0.5 break-words text-gray-800 dark:text-gray-200">{kiosk.location || kiosk.region || "Not set"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 dark:text-gray-400">Type</dt>
                        <dd className="mt-0.5 text-gray-800 dark:text-gray-200">{kiosk.kioskType || "PHYSICAL"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 dark:text-gray-400">Token expiry</dt>
                        <dd className="mt-0.5 text-gray-800 dark:text-gray-200">{Number(kiosk.tokenExpiryHours || 1)} hours</dd>
                      </div>
                      {kiosk.region && kiosk.location && (
                        <div>
                          <dt className="text-xs text-gray-500 dark:text-gray-400">Region</dt>
                          <dd className="mt-0.5 break-words text-gray-800 dark:text-gray-200">{kiosk.region}</dd>
                        </div>
                      )}
                    </dl>
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3 dark:border-white/10">
                      <ActionIconButton
                        label="Edit kiosk"
                        icon={<FaEdit className="h-4 w-4" />}
                        loading={actionLoadingKey === `edit-${kiosk.kioskId}`}
                        disabled={actionLoadingKey !== null && actionLoadingKey !== `edit-${kiosk.kioskId}`}
                        onClick={() => {
                          setCreateError("");
                          setEditingKiosk(kiosk);
                        }}
                      />
                      <ActionIconButton
                        label="Rotate secret"
                        icon={<FaKey className="h-4 w-4" />}
                        loading={actionLoadingKey === `rotate-${kiosk.kioskId}`}
                        disabled={actionLoadingKey !== null && actionLoadingKey !== `rotate-${kiosk.kioskId}`}
                        onClick={() => {
                          setRotateTarget(kiosk);
                          setRotateTokenExpiryHours(Number(kiosk.tokenExpiryHours || 1));
                          setRotateValidationError("");
                        }}
                      />
                      <ActionIconButton
                        label="Copy link"
                        icon={<FaLink className="h-4 w-4" />}
                        loading={actionLoadingKey === `weblink-${kiosk.kioskId}`}
                        disabled={actionLoadingKey !== null && actionLoadingKey !== `weblink-${kiosk.kioskId}`}
                        onClick={() => handleGenerateWebLink(kiosk)}
                      />
                      <ActionIconButton
                        label={`Manage S710 devices for ${kiosk.kioskId}`}
                        icon={<FaMobileAlt className="h-4 w-4" />}
                        disabled={actionLoadingKey !== null}
                        onClick={() => setS710DevicesTarget(kiosk)}
                      />
                      <ActionIconButton
                        label="Copy TV link"
                        icon={<FaTv className="h-4 w-4" />}
                        loading={actionLoadingKey === `tvlink-${kiosk.kioskId}`}
                        disabled={actionLoadingKey !== null && actionLoadingKey !== `tvlink-${kiosk.kioskId}`}
                        onClick={() => {
                          void handleCopyTvLink(kiosk);
                        }}
                      />
                      <ActionIconButton
                        label="Download QR"
                        icon={<FaQrcode className="h-4 w-4" />}
                        loading={actionLoadingKey === `qr-${kiosk.kioskId}`}
                        disabled={actionLoadingKey !== null && actionLoadingKey !== `qr-${kiosk.kioskId}`}
                        onClick={() => handleGenerateQr(kiosk)}
                      />
                      <ActionIconButton
                        label="Live impact & embed"
                        icon={<FaCode className="h-4 w-4" />}
                        disabled={actionLoadingKey !== null}
                        onClick={() => handleOpenImpactEmbed(kiosk)}
                      />
                    </div>
                  </article>
                ))}
              </div>
              <div className="hidden md:block">
                <Table
                  data={paginatedKiosks}
                  columns={columns}
                  loading={loadingList}
                  hoverable={false}
                  emptyMessage="No kiosks found."
                  rowKey={(row) => row.kioskId}
                />
              </div>
            </>
          ) : (
            <div className="max-h-[calc(100vh-270px)] overflow-auto rounded-lg border border-gray-200/70 bg-white dark:border-white/10 dark:bg-slate-900/20">
              <Table
                data={paginatedKiosks}
                columns={columns}
                loading={loadingList}
                hoverable={false}
                emptyMessage="No kiosks found. Add a kiosk to get started."
                rowKey={(row) => row.kioskId}
              />
            </div>
          )}
        </div>
      </PageLayout>

      <AddKioskModal
        open={showAddModal || Boolean(editingKiosk)}
        loading={creating}
        error={createError}
        mode={editingKiosk ? "edit" : "create"}
        isKioskOwner={isKioskOwner}
        initialData={editingKiosk}
        onClose={() => {
          if (!creating) {
            setShowAddModal(false);
            setEditingKiosk(null);
            setCreateError("");
          }
        }}
        onConfirm={editingKiosk ? handleUpdateKiosk : handleCreateKiosk}
      />

      <SecretModal
        open={secretModal.open}
        title={secretModal.title}
        secret={secretModal.secret}
        onClose={() => setSecretModal({ open: false, title: "", secret: "" })}
      />

      <WebLinkModal
        open={webLinkModal.open}
        title={webLinkModal.title}
        webLink={webLinkModal.webLink}
        kioskSecret={webLinkModal.kioskSecret}
        impactShareUrl={webLinkModal.impactShareUrl}
        onClose={() =>
          setWebLinkModal({
            open: false,
            title: "",
            webLink: "",
            kioskSecret: "",
            impactShareUrl: "",
          })
        }
      />

      <WebQrModal
        open={webQrModal.open}
        title={webQrModal.title}
        webLink={webQrModal.webLink}
        kioskSecret={webQrModal.kioskSecret}
        onClose={() =>
          setWebQrModal({
            open: false,
            title: "",
            webLink: "",
            kioskSecret: "",
          })
        }
      />

      <ImpactEmbedModal
        open={impactEmbedModal.open}
        kioskId={impactEmbedModal.kioskId}
        kioskName={impactEmbedModal.kioskName}
        widgetUrl={impactEmbedModal.widgetUrl}
        onClose={() =>
          setImpactEmbedModal({
            open: false,
            kioskId: "",
            kioskName: "",
            widgetUrl: "",
          })
        }
      />

      <S710DevicesModal
        open={Boolean(s710DevicesTarget)}
        kiosk={s710DevicesTarget}
        onClose={() => setS710DevicesTarget(null)}
      />

      <ConfirmModal
        open={Boolean(rotateTarget)}
        onClose={() => setRotateTarget(null)}
        onConfirm={handleRotateSecret}
        title="Rotate Kiosk Secret"
        message={`Are you sure you want to rotate the secret for kiosk ${rotateTarget?.kioskId || ""}? The old secret will stop working immediately.`}
        confirmLabel="Rotate Secret"
        confirmVariant="primary"
        body={
          <div className="space-y-2">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              New Token Expiry (Hours)
            </label>
            <input
              type="number"
              min={1}
              max={720}
              step={1}
              value={rotateTokenExpiryHours}
              onChange={(event) => {
                setRotateTokenExpiryHours(Number(event.target.value || 0));
                setRotateValidationError("");
              }}
              className="w-full rounded-md border border-gray-300 p-2 focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              placeholder="e.g. 2"
            />
            {rotateValidationError && (
              <p className="text-sm text-red-500">{rotateValidationError}</p>
            )}
          </div>
        }
      />

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteKiosk}
        title="Delete Kiosk"
        message={`Are you sure you want to delete kiosk ${deleteTarget?.kioskId || ""}? This cannot be undone easily.`}
        confirmLabel="Delete"
        confirmVariant="secondary"
      />
    </>
  );
};

export default KioskManagementPage;
